import React, { useState, useEffect } from 'react';
import {
    getRundschreibenUpdates,
    getBaFinRundschreiben,
    getBundesbankRundschreiben,
    checkRundschreibenFiles,
    handleSecureDownload,
    getRundschreibenAIAnalysis
} from '../../../../../services/api';
import './NeuigkeitenSection.css';

const NeuigkeitenSection = () => {
    const [updateInfo, setUpdateInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // States für Rundschreiben-Listen
    const [selectedSource, setSelectedSource] = useState(null);
    const [rundschreibenData, setRundschreibenData] = useState(null);
    const [rundschreibenLoading, setRundschreibenLoading] = useState(false);
    const [rundschreibenError, setRundschreibenError] = useState(null);

    // States für KI-Auswertung
    const [expandedAI, setExpandedAI] = useState({});
    const [aiData, setAiData] = useState({});
    const [loadingAI, setLoadingAI] = useState({});

    // API-Call zum Laden der Update-Informationen
    const fetchUpdateInfo = async () => {
        try {
            setLoading(true);
            setError(null);

            const result = await getRundschreibenUpdates();

            if (result.success) {
                setUpdateInfo(result.data);
            } else {
                throw new Error(result.error || 'Unbekannter Fehler');
            }
        } catch (err) {
            console.error('Fehler beim Laden der Update-Informationen:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    // Funktion zum Laden der Rundschreiben-Daten mit Dateistruktur-Check
    const fetchRundschreibenData = async (source) => {
        try {
            setRundschreibenLoading(true);
            setRundschreibenError(null);

            let result;
            if (source === 'bafin') {
                result = await getBaFinRundschreiben();
            } else if (source === 'bundesbank') {
                result = await getBundesbankRundschreiben();
            }

            if (result.success) {
                // Sortiere nach Datum und nimm die ersten 15
                const sortedData = result.data.data.sort((a, b) => {
                    const dateA = parseGermanDate(a.datum);
                    const dateB = parseGermanDate(b.datum);
                    return dateB - dateA;
                }).slice(0, 15);

                // Für jedes Rundschreiben: Prüfe verfügbare Dateien und KI-Auswertung
                const enrichedData = await Promise.all(sortedData.map(async (item) => {
                    const fileInfo = await checkAvailableFiles(item.ordner, source);
                    return {
                        ...item,
                        hasDownloadableFiles: fileInfo.hasDownloadableFiles,
                        hasAIAnalysis: fileInfo.hasAIAnalysis,
                        downloadableFiles: fileInfo.downloadableFiles
                    };
                }));

                setRundschreibenData({
                    ...result.data,
                    data: enrichedData,
                    source: source
                });
            } else {
                throw new Error(result.error || 'Fehler beim Laden der Rundschreiben');
            }
        } catch (err) {
            console.error(`Fehler beim Laden der ${source} Rundschreiben:`, err);
            setRundschreibenError(err.message);
        } finally {
            setRundschreibenLoading(false);
        }
    };

    // Prüft verfügbare Dateien für ein Rundschreiben (SICHER über Backend)
    const checkAvailableFiles = async (ordnerPfad, source) => {
        if (!ordnerPfad) {
            return { hasDownloadableFiles: false, hasAIAnalysis: false, downloadableFiles: [] };
        }

        try {
            const result = await checkRundschreibenFiles(source, ordnerPfad);
            return result;
        } catch (error) {
            //console.log(`Dateicheck für ${ordnerPfad} fehlgeschlagen:`, error);
            return { hasDownloadableFiles: false, hasAIAnalysis: false, downloadableFiles: [] };
        }
    };

    // Lädt KI-Auswertung für ein Rundschreiben (SICHER über Backend)
    const loadAIAnalysis = async (item, source) => {
        const itemKey = `${source}_${item.nummer}`;

        // ALWAYS reload - no caching für debugging
        // if (aiData[itemKey]) {
        //     // Bereits geladen, nur ein/ausklappen
        //     setExpandedAI(prev => ({
        //         ...prev,
        //         [itemKey]: !prev[itemKey]
        //     }));
        //     return;
        // }

        try {
            setLoadingAI(prev => ({ ...prev, [itemKey]: true }));

            const auswertungFolder = source === 'bafin' ? 'auswertung_bafin_gesamt' : 'auswertung_buba_gesamt';

            // console.log('Lade KI-Auswertung für:', {
            //     source,
            //     ordner: item.ordner,
            //     auswertungFolder,
            //     itemKey,
            //     titel: item.titel,
            //     datum: item.datum
            // });

            // FORCE fresh request - add timestamp to prevent caching
            const timestamp = Date.now();
            const result = await getRundschreibenAIAnalysis(source, item.ordner, auswertungFolder, `auswertung.txt?t=${timestamp}`);

            if (result.success) {
                // console.log('KI-Auswertung korrekt geladen für:', {
                //     itemKey,
                //     titel: item.titel,
                //     zusammenfassung_start: result.data.zusammenfassung?.substring(0, 150) + '...',
                //     hat_zuordnung: !!result.data.zuordnung,
                //     anzahl_institute: Object.keys(result.data.zuordnung || {}).length,
                //     hat_empfehlungen: !!result.data.empfehlungen,
                //     anzahl_empfehlungen: result.data.empfehlungen?.length || 0
                // });

                setAiData(prev => ({
                    ...prev,
                    [itemKey]: result.data
                }));
                setExpandedAI(prev => ({
                    ...prev,
                    [itemKey]: true
                }));
            } else {
                console.error('❌ KI-Auswertung Fehler:', result.error);
                setAiData(prev => ({
                    ...prev,
                    [itemKey]: { error: result.error || 'Auswertung nicht verfügbar' }
                }));
            }
        } catch (error) {
            console.error('❌ Exception beim Laden der KI-Auswertung:', error);
            setAiData(prev => ({
                ...prev,
                [itemKey]: { error: 'Fehler beim Laden der Auswertung' }
            }));
        } finally {
            setLoadingAI(prev => ({ ...prev, [itemKey]: false }));
        }
    };

    // Download von gefilterten Dateien (SICHER über Backend)
    const handleDownload = async (item, source) => {
        if (!item.hasDownloadableFiles) return;

        try {
            const result = await handleSecureDownload(source, item.ordner, item.nummer);

            if (!result.success) {
                alert(`Download fehlgeschlagen: ${result.error}`);
            }
        } catch (error) {
            console.error('Download error:', error);
            alert('Download-Fehler aufgetreten');
        }
    };

    // Hilfsfunktion zum Parsen des deutschen Datums
    const parseGermanDate = (dateString) => {
        if (!dateString) return new Date(0);
        const [day, month, year] = dateString.split('.');
        return new Date(year, month - 1, day);
    };

    useEffect(() => {
        fetchUpdateInfo();
        const interval = setInterval(fetchUpdateInfo, 30 * 60 * 1000);
        return () => clearInterval(interval);
    }, []);

    const handleRundschreibenClick = (typ) => {
        const source = typ.toLowerCase();

        if (selectedSource === source) {
            setSelectedSource(null);
            setRundschreibenData(null);
            return;
        }

        setSelectedSource(source);
        fetchRundschreibenData(source);
    };

    const formatDate = (isoString) => {
        if (!isoString) return 'Unbekannt';
        try {
            const date = new Date(isoString);
            return date.toLocaleString('de-DE', {
                day: '2-digit',
                month: 'long',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                timeZone: 'Europe/Berlin'
            });
        } catch (err) {
            return 'Ungültiges Datum';
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'available': return '🟢';
            case 'error': return '🟡';
            case 'unavailable': return '🔴';
            default: return '⚪';
        }
    };

    const getStatusText = (status) => {
        switch (status) {
            case 'available': return 'Verfügbar';
            case 'error': return 'Fehler';
            case 'unavailable': return 'Nicht verfügbar';
            default: return 'Unbekannt';
        }
    };

    const renderUpdateStatus = () => {
        if (loading) {
            return (
                <div className="update-status loading">
                    <span>Lade Update-Informationen...</span>
                </div>
            );
        }

        if (error) {
            return (
                <div className="update-status error">
                    <span>Fehler beim Laden: {error}</span>
                    <button
                        className="retry-button"
                        onClick={fetchUpdateInfo}
                        title="Erneut versuchen"
                    >
                        ↻
                    </button>
                </div>
            );
        }

        if (!updateInfo) {
            return (
                <div className="update-status">
                    <span>Keine Update-Informationen verfügbar</span>
                </div>
            );
        }

        const lastUpdate = updateInfo.last_update
            ? formatDate(updateInfo.last_update)
            : 'Kein aktuelles Update verfügbar';

        return (
            <div className="update-status">
                <div className="status-header">
                    <span className="status-text">Stand: {lastUpdate}</span>
                    <button
                        className="refresh-button"
                        onClick={fetchUpdateInfo}
                        title="Aktualisieren"
                        disabled={loading}
                    >
                        ↻
                    </button>
                </div>

                <div className="sources-status">
                    <div className="source-item">
                        <span className="source-icon">{getStatusIcon(updateInfo.bafin.status)}</span>
                        <span className="source-name">BaFin</span>
                        <span className="source-status">{getStatusText(updateInfo.bafin.status)}</span>
                        {updateInfo.bafin.last_modified && (
                            <span className="source-date">
                                {formatDate(updateInfo.bafin.last_modified)}
                            </span>
                        )}
                    </div>

                    <div className="source-item">
                        <span className="source-icon">{getStatusIcon(updateInfo.bundesbank.status)}</span>
                        <span className="source-name">Bundesbank</span>
                        <span className="source-status">{getStatusText(updateInfo.bundesbank.status)}</span>
                        {updateInfo.bundesbank.last_modified && (
                            <span className="source-date">
                                {formatDate(updateInfo.bundesbank.last_modified)}
                            </span>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    // Verbesserte KI-Auswertung Render-Funktion
    const renderAIAnalysis = (aiAnalysis) => {
        if (aiAnalysis.error) {
            return (
                <div className="ai-error">
                    <span>⚠️ {aiAnalysis.error}</span>
                </div>
            );
        }

        return (
            <div className="ai-analysis-content">
                {aiAnalysis.zusammenfassung && (
                    <div className="ai-section">
                        <h5>Zusammenfassung</h5>
                        <p>{aiAnalysis.zusammenfassung}</p>
                    </div>
                )}

                {aiAnalysis.hauptpunkte && Array.isArray(aiAnalysis.hauptpunkte) && (
                    <div className="ai-section">
                        <h5>Hauptpunkte</h5>
                        <ul>
                            {aiAnalysis.hauptpunkte.map((punkt, idx) => (
                                <li key={idx}>{punkt}</li>
                            ))}
                        </ul>
                    </div>
                )}

                {aiAnalysis.auswirkungen && (
                    <div className="ai-section">
                        <h5>Auswirkungen</h5>
                        <p>{aiAnalysis.auswirkungen}</p>
                    </div>
                )}

                {aiAnalysis.handlungsempfehlungen && Array.isArray(aiAnalysis.handlungsempfehlungen) && (
                    <div className="ai-section">
                        <h5>Handlungsempfehlungen</h5>
                        <ul>
                            {aiAnalysis.handlungsempfehlungen.map((empfehlung, idx) => (
                                <li key={idx}>{empfehlung}</li>
                            ))}
                        </ul>
                    </div>
                )}

                {/* NEUE: Empfehlungen aus JSON anzeigen */}
                {aiAnalysis.empfehlungen && Array.isArray(aiAnalysis.empfehlungen) && (
                    <div className="ai-section">
                        <h5>Empfehlungen</h5>
                        <ul>
                            {aiAnalysis.empfehlungen.map((empfehlung, idx) => (
                                <li key={idx}>{empfehlung}</li>
                            ))}
                        </ul>
                    </div>
                )}

                {/* NEUE: Zuordnung zu Institutsarten anzeigen */}
                {aiAnalysis.zuordnung && typeof aiAnalysis.zuordnung === 'object' && (
                    <div className="ai-section">
                        <h5>Zuordnung zu Institutsarten</h5>
                        <div className="zuordnung-grid">
                            {Object.entries(aiAnalysis.zuordnung).map(([institut, beschreibung]) => {
                                // Dreistufige Logik für Betroffenheit
                                const text = beschreibung.toLowerCase();

                                let statusType = 'nicht-betroffen'; // Standard: GRÜN
                                let statusIcon = '○';

                                // GRÜN (nicht betroffen) - klare Verneinung
                                if (text.includes('nicht betroffen') ||
                                    text.includes('keine relevanz') ||
                                    text.includes('keine adressierten anforderungen') ||
                                    text.includes('werden nicht erwähnt') ||
                                    text.includes('sind nicht angesprochen') ||
                                    text.includes('keine speziellen vorgaben') ||
                                    text.includes('keine regelungen')) {
                                    statusType = 'nicht-betroffen';
                                    statusIcon = '○';
                                }
                                // ROT (betroffen) - klare Betroffenheit
                                else if ((text.includes('betroffen') && !text.includes('nicht')) ||
                                    text.includes('müssen') ||
                                    text.includes('sind verpflichtet') ||
                                    text.includes('unterliegen') ||
                                    text.includes('relevante anforderungen') ||
                                    text.includes('anforderungen für institute') ||
                                    text.includes('vorgaben für') ||
                                    text.includes('regelungen für')) {
                                    statusType = 'betroffen';
                                    statusIcon = '●';
                                }
                                // GELB (unklar) - mehrdeutige Fälle
                                else if (text.includes('relevant') && !text.includes('keine relevanz')) {
                                    statusType = 'unklar';
                                    statusIcon = '◐';
                                }

                                return (
                                    <div key={institut} className={`zuordnung-item ${statusType}`}>
                                        <div className="institut-name">
                                            <span className={`status-indicator ${statusType}`}>
                                                {statusIcon}
                                            </span>
                                            {institut.toUpperCase()}
                                        </div>
                                        <div className="institut-beschreibung">
                                            {beschreibung}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {aiAnalysis.relevanz && (
                    <div className="ai-section">
                        <h5>Relevanz-Einschätzung</h5>
                        <div className={`relevanz-badge ${aiAnalysis.relevanz.toLowerCase()}`}>
                            {aiAnalysis.relevanz}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    // Rundschreiben-Liste
    const renderRundschreibenList = () => {
        if (!selectedSource) return null;

        return (
            <div className="rundschreiben-section">
                <div className="rundschreiben-header">
                    <h3>
                        {selectedSource === 'bafin' ? 'BaFin' : 'Bundesbank'} Rundschreiben -
                        Neueste 15 Einträge
                    </h3>
                    <button
                        className="close-button"
                        onClick={() => {
                            setSelectedSource(null);
                            setRundschreibenData(null);
                            // KI-Daten Cache leeren bei Schließen
                            setAiData({});
                            setExpandedAI({});
                        }}
                        title="Schließen"
                    >
                        ✕
                    </button>
                </div>

                {rundschreibenLoading && (
                    <div className="loading-state">
                        <span>Lade Rundschreiben...</span>
                    </div>
                )}

                {rundschreibenError && (
                    <div className="error-state">
                        <span>Fehler: {rundschreibenError}</span>
                        <button
                            className="retry-button"
                            onClick={() => fetchRundschreibenData(selectedSource)}
                        >
                            Erneut versuchen
                        </button>
                    </div>
                )}

                {rundschreibenData && !rundschreibenLoading && (
                    <div className="rundschreiben-list">
                        {rundschreibenData.data.map((item, index) => {
                            const itemKey = `${selectedSource}_${item.nummer}`;
                            const isAIExpanded = expandedAI[itemKey];
                            const isAILoading = loadingAI[itemKey];
                            const currentAIData = aiData[itemKey];

                            return (
                                <div key={index} className="rundschreiben-item">
                                    <div className="item-header">
                                        <div className="item-number">
                                            {item.nummer || "-"}
                                        </div>
                                        <div className="item-date">
                                            {item.datum}
                                        </div>
                                    </div>

                                    <div className="item-content">
                                        <h4 className="item-title">
                                            {item.titel}
                                        </h4>

                                        {selectedSource === 'bundesbank' && item.untertitel && (
                                            <p className="item-subtitle">
                                                {item.untertitel}
                                            </p>
                                        )}

                                        {selectedSource === 'bafin' && item.bereich && (
                                            <div className="item-meta">
                                                <span className="meta-label">Bereich:</span>
                                                <span className="meta-value">{item.bereich}</span>
                                            </div>
                                        )}

                                        {selectedSource === 'bafin' && item.thema && (
                                            <div className="item-meta">
                                                <span className="meta-label">Thema:</span>
                                                <span className="meta-value">{item.thema}</span>
                                            </div>
                                        )}

                                        {selectedSource === 'bafin' && item.zusammenfassung && (
                                            <p className="item-summary">
                                                {item.zusammenfassung}
                                            </p>
                                        )}
                                    </div>

                                    <div className="item-actions">
                                        <button
                                            className={`download-button ${!item.hasDownloadableFiles ? 'disabled' : ''}`}
                                            onClick={() => handleDownload(item, selectedSource)}
                                            disabled={!item.hasDownloadableFiles}
                                            title={item.hasDownloadableFiles ? 'Dokumente herunterladen (HTML/PDF)' : 'Keine downloadbaren Dokumente verfügbar'}
                                        >
                                            📄 Dokumente ({item.downloadableFiles?.length || 0})
                                        </button>

                                        <button
                                            className={`ai-button ${!item.hasAIAnalysis ? 'disabled' : ''} ${isAIExpanded ? 'active' : ''}`}
                                            onClick={() => loadAIAnalysis(item, selectedSource)}
                                            disabled={!item.hasAIAnalysis || isAILoading}
                                            title={item.hasAIAnalysis ? 'KI-basierte Auswertung anzeigen' : 'Keine KI-Auswertung verfügbar'}
                                        >
                                            {isAILoading ? '⏳' : '🪄'} KI-Auswertung
                                        </button>
                                    </div>

                                    {/* KI-Auswertung Bereich */}
                                    {isAIExpanded && currentAIData && (
                                        <div className="ai-analysis-section">
                                            <div className="ai-analysis-header">
                                                <h5>KI-basierte Analyse</h5>
                                                <button
                                                    className="collapse-button"
                                                    onClick={() => setExpandedAI(prev => ({
                                                        ...prev,
                                                        [itemKey]: false
                                                    }))}
                                                >
                                                    ↑
                                                </button>
                                            </div>
                                            {renderAIAnalysis(currentAIData)}
                                        </div>
                                    )}
                                </div>
                            );
                        })}

                        {rundschreibenData.data.length === 0 && (
                            <div className="no-data">
                                Keine Rundschreiben gefunden.
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="neuigkeiten-section">
            <div className="section-header">
                <h2>Neuigkeiten / Updates</h2>
                {renderUpdateStatus()}
            </div>

            <div className="updates-table">
                <div className="table-header">
                    <div className="col-header">Rundschreiben</div>
                    <div className="col-header">Merkblätter</div>
                    <div className="col-header">Gesetze / Richtlinien</div>
                </div>

                <div className="table-row">
                    <div className="table-cell">
                        <div className="update-item">
                            <button
                                className={`update-link ${
                                    updateInfo?.bafin.status !== 'available' ? 'disabled' : ''
                                } ${selectedSource === 'bafin' ? 'active' : ''}`}
                                onClick={() => handleRundschreibenClick('BaFin')}
                                disabled={updateInfo?.bafin.status !== 'available'}
                            >
                                <span className="update-title">
                                    {getStatusIcon(updateInfo?.bafin.status || 'unknown')}
                                    &nbsp;BaFin Rundschreiben
                                </span>
                                <span className="update-indicator">
                                    {selectedSource === 'bafin' ? '↓' : '→'}
                                </span>
                            </button>

                            <button
                                className={`update-link ${
                                    updateInfo?.bundesbank.status !== 'available' ? 'disabled' : ''
                                } ${selectedSource === 'bundesbank' ? 'active' : ''}`}
                                onClick={() => handleRundschreibenClick('Bundesbank')}
                                disabled={updateInfo?.bundesbank.status !== 'available'}
                            >
                                <span className="update-title">
                                    {getStatusIcon(updateInfo?.bundesbank.status || 'unknown')}
                                    &nbsp;Bundesbank Rundschreiben
                                </span>
                                <span className="update-indicator">
                                    {selectedSource === 'bundesbank' ? '↓' : '→'}
                                </span>
                            </button>
                        </div>
                    </div>
                    <div className="table-cell">
                        <span className="no-updates">...</span>
                    </div>
                    <div className="table-cell">
                        <span className="no-updates">...</span>
                    </div>
                </div>
            </div>

            {renderRundschreibenList()}
        </div>
    );
};

export default NeuigkeitenSection;